'use client';

import React, { useMemo, useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { IconExpandDiagonal, IconLockClosed, IconShrinkDiagonal } from '@repo/svgs';
import { useStore } from '@/providers/StoreProvider';
import { useAuthModal } from '@/hooks/useAuthModal';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useAnalytics } from '@/hooks/useAnalytics';
import { SectionHeader } from '@/components/ui';

export interface ReportActionBarProps {
  /** Optional className to customize container */
  className?: string;
}

export function ReportActionBar({ className }: ReportActionBarProps) {
  const { openAuthFlow } = useAuthModal();
  const isLoggedIn = useStore(state => state.webAuth.isLoggedIn);
  const [isExpanded, setIsExpanded] = useState(true);

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const analytics = useAnalytics();
  const currentPath = useMemo(() => {
    const qs = searchParams.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  }, [pathname, searchParams]);

  const handleLogin = () => {
    void openAuthFlow();
  };

  const handlePurchase = () => {
    analytics.track('get_platform_access', { page: 'building-report' });
    router.push(`/subscription?returnTo=${encodeURIComponent(currentPath)}&source=building-report`);
  };

  return (
    <div
      className={cn(
        'bg-primary flex flex-col items-start w-full relative overflow-hidden',
        'shadow-md-reverse md:shadow',
        className,
      )}
    >
      <div className={cn('flex flex-col items-start justify-center pb-4 pt-1 px-4 w-full')}>
        <div className={cn('flex flex-col items-start justify-center px-0 py-2 w-full')}>
          <div className={cn('flex gap-1 items-center justify-between w-full px-2 md:px-0')}>
            <SectionHeader className="-mx-2" title="What this building's record says" />
            {isExpanded ? (
              <IconShrinkDiagonal
                className="w-4 h-4 icon-primary md:hidden"
                onClick={() => setIsExpanded(!isExpanded)}
              />
            ) : (
              <IconExpandDiagonal
                className="w-4 h-4 icon-primary md:hidden"
                onClick={() => setIsExpanded(!isExpanded)}
              />
            )}
          </div>

          <div className={cn('flex flex-col gap-4 items-start px-2 py-0 w-full')}>
            {isExpanded ? (
              <p className="text-body-2 text-primary w-full">
                Violations, complaints, infestations, and more — pulled from official data. Unlock
                insights to see the full report.
              </p>
            ) : null}

            <div className="flex flex-col gap-2 items-start w-full">
              <div className="flex flex-col gap-4 items-start justify-center w-full">
                <Button
                  variant="primary"
                  size="md"
                  fullWidth
                  onClick={handlePurchase}
                  iconLeft={<IconLockClosed className="h-4 w-4 icon-inverted" />}
                >
                  Unlock insights
                </Button>
              </div>

              {!isLoggedIn ? (
                <div className={cn('flex gap-2 items-center justify-center w-full')}>
                  <p className="text-body-2 text-primary">Already have an account?</p>
                  <button
                    onClick={handleLogin}
                    className={cn(
                      'flex gap-1 items-center justify-center px-0 py-2',
                      'text-body-2-high text-accent text-center cursor-pointer',
                      'hover:underline focus-visible:outline-none focus-visible:underline',
                    )}
                  >
                    Log in
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
